import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Card } from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Switch from '@/components/ui/Switch';
import type { SimulationItem, SimulationWeek } from '@/api/engineService';
import { sportColor } from '@/lib/sportColors';
import WorkoutPreviewPanel from '@/features/workouts/WorkoutPreviewPanel';
import Drawer from '@/components/ui/Drawer';
import GuidanceText from '@/components/ui/GuidanceText';
import { addDaysIso, DAYS, formatFr } from './simulatorUtils';

const minutes = (sec: number) => `${Math.round(sec / 60)} min`;

interface PreviewTarget {
  workoutId: string;
  format: string | null;
}

const SessionCard = ({ item, onOpen }: { item: SimulationItem; onOpen: (target: PreviewTarget) => void }) => (
  <button
    type="button"
    disabled={!item.workout}
    onClick={() => item.workout && onOpen({ workoutId: item.workout.id, format: item.scalingFormat })}
    className="w-full rounded border border-gray-200 bg-white p-1.5 text-left text-[11px] hover:border-brand-300 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-brand-700"
    style={{ borderLeftColor: sportColor(item.sport?.id), borderLeftWidth: 3 }}
  >
    <p className="truncate font-medium text-gray-800 dark:text-gray-100">{item.workout?.name}</p>
    <p className="flex items-center justify-between gap-1 text-gray-400">
      <span>{item.workout ? minutes(item.workout.durationSec) : ''}</span>
      <span className="flex items-center gap-1">
        {item.moved && <span className="rounded bg-warning-500/10 px-1 text-warning-500">décalée</span>}
        {item.isTest ? (
          <Badge color="yellow">Test{item.priority ? ` · P${item.priority}` : ''}</Badge>
        ) : item.priority ? (
          <span className="rounded bg-gray-100 px-1 dark:bg-gray-800">P{item.priority}</span>
        ) : null}
      </span>
    </p>
  </button>
);

/** R44: the competition is drawn from the enrollment's date, not from a generated item. */
const WeekCalendar = ({
  weeks,
  competitionDate,
  competitionCoachNote,
  showDropped,
  onShowDroppedChange,
}: {
  weeks: SimulationWeek[];
  competitionDate?: string;
  competitionCoachNote?: string | null;
  showDropped: boolean;
  onShowDroppedChange: (value: boolean) => void;
}) => {
  const [index, setIndex] = useState(0);
  const [showTrace, setShowTrace] = useState(false);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [competitionOpen, setCompetitionOpen] = useState(false);
  const week = weeks[index];

  useEffect(() => setIndex(0), [weeks]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(weeks.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [weeks.length]);

  if (!week) return null;

  const links = new Map<string, SimulationItem[]>();
  week.items.forEach((i) => {
    if (i.linkKey) (links.get(i.linkKey) ?? links.set(i.linkKey, []).get(i.linkKey)!).push(i);
  });

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={index === 0} onClick={() => setIndex(index - 1)}>
            ‹
          </Button>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Semaine {index + 1} / {weeks.length}
          </span>
          <Button size="sm" variant="outline" disabled={index === weeks.length - 1} onClick={() => setIndex(index + 1)}>
            ›
          </Button>
          <Select value={index} onChange={(e) => setIndex(Number(e.target.value))}>
            {weeks.map((w, i) => (
              <option key={w.weekStart} value={i}>
                {i + 1} · {formatFr(w.weekStart)}
                {w.phase === 'BACKUP' ? ' · attente' : w.cycleRole === 'RECOVERY' ? ' · récupération' : ''}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <label className="flex items-center gap-2">
            <Switch size="sm" checked={showDropped} onChange={onShowDroppedChange} /> Afficher les séances écartées
          </label>
          <label className="flex items-center gap-2">
            <Switch size="sm" checked={showTrace} onChange={setShowTrace} /> Décisions de la semaine
          </label>
        </div>
      </div>

      <p className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        {formatFr(week.weekStart)} – {formatFr(addDaysIso(week.weekStart, 6))} · {week.cycle.name} (semaine auteur {week.sourceWeekNumber})
        {week.phase === 'BACKUP' && <Badge color="gray">Attente</Badge>}
        {week.cycleRole === 'RECOVERY' && <Badge color="green">Récupération</Badge>}
        {week.cycleRole === 'TEST' && <Badge color="yellow">Tests</Badge>}
      </p>

      <div className="grid grid-cols-7 gap-2">
        {DAYS.map((d) => {
          const date = addDaysIso(week.weekStart, d.value - 1);
          const items = week.items.filter((i) => i.date === date);
          const race = competitionDate === date;
          const singles = items.filter((i) => !i.linkKey);
          const dayLinks = [...links.entries()].filter(([, members]) => members[0].date === date);
          const dropped = showDropped ? week.dropped.filter((x) => (x.dayOfWeek ?? 7) === d.value) : [];
          return (
            <div key={d.value} className={clsx('flex min-h-[8rem] flex-col gap-1.5 rounded-md border p-1.5', race ? 'border-brand-500' : 'border-gray-200 dark:border-gray-800')}>
              <span className="text-[11px] font-medium uppercase text-gray-400">
                {d.label.slice(0, 3)} {date.slice(8, 10)}
              </span>
              {race && (
                <button
                  type="button"
                  onClick={() => setCompetitionOpen(true)}
                  className="rounded bg-brand-500 p-1.5 text-center text-xs font-semibold text-white hover:bg-brand-600"
                >
                  Compétition
                </button>
              )}
              {dayLinks.map(([key, members]) => (
                <div key={key} className="flex flex-col gap-1 rounded border border-success-500/60 bg-success-500/5 p-1">
                  <Badge color="green">Enchaînement</Badge>
                  {members.sort((a, b) => (a.linkOrder ?? 0) - (b.linkOrder ?? 0)).map((m) => <SessionCard key={m.workout?.id + m.date + m.linkOrder} item={m} onOpen={setPreview} />)}
                </div>
              ))}
              {singles.map((i, k) => (
                <SessionCard key={`${i.workout?.id}-${k}`} item={i} onOpen={setPreview} />
              ))}
              {dropped.map((x) => (
                <button
                  type="button"
                  key={x.cycleSessionId}
                  onClick={() => setPreview({ workoutId: x.workout.id, format: x.scalingFormat })}
                  className="rounded border border-dashed border-gray-300 p-1.5 text-left text-[11px] text-gray-400 hover:border-brand-300 dark:border-gray-700 dark:hover:border-brand-700"
                  style={{ borderLeftColor: sportColor(x.sport.id), borderLeftWidth: 3 }}
                >
                  <p className="truncate">{x.workout.name}</p>
                  <p>
                    P{x.priority} · {x.reasonLabel}
                  </p>
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {showTrace && (
        <ul className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600 dark:bg-gray-800/50 dark:text-gray-300">
          {week.trace.length === 0 && <li>Aucune décision particulière.</li>}
          {week.trace.map((t, i) => (
            <li key={i}>• {t.message}</li>
          ))}
        </ul>
      )}

      <WorkoutPreviewPanel workoutId={preview?.workoutId ?? null} format={preview?.format} onClose={() => setPreview(null)} />

      {/* D27: read-only, the competition coach note and nothing else. */}
      <Drawer open={competitionOpen} onClose={() => setCompetitionOpen(false)} title={`Compétition${competitionDate ? ` · ${formatFr(competitionDate)}` : ''}`}>
        <h3 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">Mot du coach</h3>
        <GuidanceText text={competitionCoachNote} placeholder="Aucun mot du coach pour ce type de course." />
      </Drawer>
    </Card>
  );
};

export default WeekCalendar;
